const API_URL = import.meta.env.VITE_API_URL || '';

export interface RotatedKey {
    /** Plain text, returned once and never retrievable again. */
    apiKey: string;
    keyPrefix: string;
}

export type KeyActionOutcome =
    | { kind: 'rotated'; value: RotatedKey }
    | { kind: 'revoked' }
    | { kind: 'refused'; message: string }
    | { kind: 'forbidden' }
    | { kind: 'error'; status: number };

export const describeKeyActionOutcome = (outcome: KeyActionOutcome): string => {
    switch (outcome.kind) {
        case 'rotated':
        case 'revoked':
            return '';
        case 'forbidden':
            return 'You are not authorized to change device API keys.';
        case 'refused':
            return outcome.message;
        case 'error':
            return `The request failed with HTTP ${outcome.status}.`;
    }
};

const readFailure = async (
    response: { status: number; json: () => Promise<unknown> },
): Promise<KeyActionOutcome> => {
    if (response.status === 403 || response.status === 401) {
        return { kind: 'forbidden' };
    }

    if (response.status === 400) {
        // The admin routes answer a domain refusal as 400 with { message }. Surfacing that text
        // is the point: "no active key to rotate" is a different problem from a server fault and
        // the operator can act on it.
        try {
            const body = (await response.json()) as { message?: string };
            if (body?.message) {
                return { kind: 'refused', message: body.message };
            }
        } catch {
            // A 400 with no readable body is still a refusal, just an unhelpful one. Fall
            // through rather than reporting it as a transport error, which it is not.
        }

        return { kind: 'refused', message: 'The server refused the request.' };
    }

    return { kind: 'error', status: response.status };
};

export const readRotateResponse = async (
    response: { status: number; json: () => Promise<unknown> },
): Promise<KeyActionOutcome> => {
    if (response.status === 200) {
        return { kind: 'rotated', value: (await response.json()) as RotatedKey };
    }

    return readFailure(response);
};

export const readRevokeResponse = async (
    response: { status: number; json: () => Promise<unknown> },
): Promise<KeyActionOutcome> => {
    if (response.status === 200 || response.status === 204) {
        return { kind: 'revoked' };
    }

    return readFailure(response);
};

export const rotateDeviceKey = async (nodeId: string): Promise<KeyActionOutcome> => {
    const response = await fetch(`${API_URL}/api/admin/devices/${nodeId}/keys/rotate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
    });

    return readRotateResponse(response);
};

export const revokeDeviceKey = async (
    nodeId: string,
    keyId: number,
    reason: string,
): Promise<KeyActionOutcome> => {
    // The key id alone is not enough. #1036: RevokeKeyCommand is scoped to the node precisely
    // because pairing your own nodeId with someone else's keyId used to revoke their key. The
    // node therefore belongs in the path, not only in the body.
    const response = await fetch(`${API_URL}/api/admin/devices/${nodeId}/keys/${keyId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
    });

    return readRevokeResponse(response);
};
